import i18n from '../i18n';
import { createToaster } from '@meforma/vue-toaster';
import Cookies from 'js-cookie';
import { generateRandomString } from '@/helpers/stringUtiles';
import { TIPO_CODIGO_DESCUENTO } from '@/constants/reservaConstants';
import currency from 'currency.js';
import moment from 'moment';
import UsuarioApiService from '@/services/api/skiandnight/UsuarioApiService';
import { calcularEdad } from '@/helpers/dateUtiles';

const { t } = i18n.global;

export async function saveComercialId(comercialId) {
  if (comercialId) {
    await UsuarioApiService.addAccesoLink(comercialId);
    Cookies.set('comercialId', comercialId, { expires: 7 });
  }
}

export function groupServiciosByCupoMetaServicio(agrupacionesServicios) {
  return Object.values(agrupacionesServicios)
    .reduce((accum, sc) => accum.concat(sc.servicios), [])
    .reduce((accum, servicio) => {
      if (!servicio.nulo) {
        servicio.cuposMetasId.forEach(cupoId => {
          if (accum[cupoId] === undefined) {
            accum[cupoId] = [];
          }
          accum[cupoId].push(servicio);
        });
      }
      return accum;
    }, {});
}

function hasIntervalosInterseccion(intervalo1, intervalo2) {
  const inicioInterseccion = Math.max(intervalo1.edadMin, intervalo2.edadMin);
  const finInterseccion = Math.min(intervalo1.edadMax, intervalo2.edadMax);

  if (inicioInterseccion > finInterseccion) {
    return false;
  }

  const duracionInterseccion = finInterseccion - inicioInterseccion + 1;

  return duracionInterseccion >= 1;
}

function gatherTarifasEdad(agrupacionesServicios) {
  const tarifasEdad = new Map();
  for (const agrupacionServicios of agrupacionesServicios) {
    for (const servicio of agrupacionServicios.servicios) {
      if (servicio.tarifaEdad && !tarifasEdad.has(servicio.tarifaEdad.id)) {
        tarifasEdad.set(servicio.tarifaEdad.id, servicio.tarifaEdad);
      } else if(!servicio.tarifaEdad && !tarifasEdad.has(null)) {
        tarifasEdad.set(null, {validaciones: { edadMin: 0, edadMax: 120}});
      }
    }
  }
  return Array.from(tarifasEdad.values());
}

function calcularFranjasEdadInterseccionadas(tarifasEdad) {
  if (!tarifasEdad || tarifasEdad.length === 0) {
    return [];
  }

  const puntosCorte = new Set();
  tarifasEdad.forEach(tarifa => {
    puntosCorte.add(tarifa.edadMin);
    puntosCorte.add(tarifa.edadMax + 1); // +1 para el siguiente rango
  });

  const puntosOrdenados = Array.from(puntosCorte).sort((a, b) => a - b);
  const franjas = [];
  for (let i = 0; i < puntosOrdenados.length - 1; i++) {
    const inicio = puntosOrdenados[i];
    const fin = puntosOrdenados[i + 1] - 1;
    const esFranjaValida = tarifasEdad.some(tarifa => inicio >= tarifa.edadMin && fin <= tarifa.edadMax);

    if (esFranjaValida) {
      franjas.push({edadMin: inicio, edadMax: fin});
    }
  }

  return franjas;
}

export function groupAgrupacionesServiciosByTarifa(agrupacionesServicios) {
  const intervalosEdad = calcularFranjasEdadInterseccionadas(
    gatherTarifasEdad(agrupacionesServicios).flatMap(each => each.validaciones)
  );
  const agrupacionesServiciosGroupedByTarifa = [];
  intervalosEdad.forEach(intervalo =>
    agrupacionesServiciosGroupedByTarifa.push({intervalo, agrupacionesServicios: []})
  );
  // En primer lugar creamos las agrupaciones por tarifa con los correspondientes servicios
	const serviciosSinTarifaByAgrupacion = new Map();
  for (const agrupacionServicios of agrupacionesServicios) {
    for (const servicio of agrupacionServicios.servicios) {
			if (servicio.tarifaEdad) {
        for (const agrupacionesServiciosGrouped of agrupacionesServiciosGroupedByTarifa) {
          if (servicio.tarifaEdad.validaciones.some(
            validacion => hasIntervalosInterseccion(agrupacionesServiciosGrouped.intervalo, validacion)
          )) {
            const agrupacionAux = agrupacionesServiciosGrouped.agrupacionesServicios
              .find(agrupacion => agrupacion.id === agrupacionServicios.id);
            if (agrupacionAux) {
              agrupacionAux.servicios.push({
                ...servicio,
                servicioOriginal: servicio,
              });
            } else {
              agrupacionesServiciosGrouped.agrupacionesServicios
                .push({
                  ...agrupacionServicios,
                  servicios: [{
                    ...servicio,
                    servicioOriginal: servicio,
                  }]
                });
            }
          }
        }
			} else {
        // Si el servicio no tiene tarifa lo guardamos para despues agregarlo a la/s agrupacion/es correspondiente
				if (serviciosSinTarifaByAgrupacion.has(agrupacionServicios.id)) {
					serviciosSinTarifaByAgrupacion.get(agrupacionServicios.id).servicios.push({
            ...servicio,
            servicioOriginal: servicio,
          });
				} else {
          serviciosSinTarifaByAgrupacion.set(
            agrupacionServicios.id,
            {
              agrupacionServicios: agrupacionServicios,
              servicios: [{
                ...servicio,
                servicioOriginal: servicio,
              }]
            }
            );
				}
			}
    }
  }

  // En caso de no haber ninguna significa que no hay servicios con tarifas de modo que creamos la de por defecto
  if (agrupacionesServiciosGroupedByTarifa.length === 0) {
    agrupacionesServiciosGroupedByTarifa.push({agrupacionesServicios: []})
  }
  
  for (const agrupacionesServiciosGrouped of agrupacionesServiciosGroupedByTarifa) {
    // En primer lugar agregamos todos los servicios que no tienen tarifa
    for (const [agrupacionId, agrupacionSinTarifa] of serviciosSinTarifaByAgrupacion) {
      const agrupacionServiciosGrouped = agrupacionesServiciosGrouped.agrupacionesServicios
        .find(agrupacion => agrupacion.id === agrupacionId);
      const serviciosCopy = agrupacionSinTarifa.servicios.map(servicio => ({...servicio, servicioOriginal: servicio.servicioOriginal }));
      if (!agrupacionServiciosGrouped) {
        agrupacionesServiciosGrouped.agrupacionesServicios.push({
          ...agrupacionSinTarifa.agrupacionServicios,
          servicios: serviciosCopy,
        });
      } else {
        agrupacionServiciosGrouped.servicios.push(...serviciosCopy);
      }
    }
    // Recalculamos el precio de cada servicio con respecto a su referente
    for (const agrupacionServiciosGrouped of agrupacionesServiciosGrouped.agrupacionesServicios) {
      let servicioReferente = null;
      if (agrupacionServiciosGrouped.paqueteBasico) {
        servicioReferente = agrupacionServiciosGrouped.servicios.find(s => s.paqueteBasico) ?? null;
      }
      if (!servicioReferente) {
        servicioReferente = agrupacionServiciosGrouped.servicios?.reduce((accum, s) => {
					if (!s.activo) return accum;
          else if (accum === null) return s;
          else if (s.precioTeorico < accum.precioTeorico) return s;
          else return accum;
        }, null);
      }
			if (servicioReferente) {
        agrupacionServiciosGrouped.servicios?.forEach(servicio => {
					if (servicio.id === servicioReferente.id) {
						servicio.precio = 0;
						if (agrupacionServiciosGrouped.paqueteBasico) {
							servicio.paqueteBasico = true;
							servicio.obligatorio = true;
						}
					} else {
						servicio.precio = currency(servicio.precioTeorico).subtract(servicioReferente.precioTeorico).value;
					}
				});
			}
      agrupacionServiciosGrouped.servicios.sort((a, b) => a.precio - b.precio);
    }
  }

  return agrupacionesServiciosGroupedByTarifa;
}

export function getAgrupacionServiciosGroupedByTarifaByEdad(agrupacionesServiciosGroupedByTarifa, edad) {
  return agrupacionesServiciosGroupedByTarifa
    .find(agrupacionesGrouped => {
      if (!agrupacionesGrouped.intervalo) {
        return true;
      } else {
        return agrupacionesGrouped.intervalo.edadMin <= edad && edad <= agrupacionesGrouped.intervalo.edadMax;
      }
    }) ?? null;
}

export function calculaPrecioDesdeForEdad(precios, reserva) {
  const edad = calcularEdad(reserva.usuario.fechaNacimiento);
  return precios.find(each => each.minEdad <= edad && each.maxEdad > edad)?.precioDesde || 0;
}

export function calculaDuracionViaje(viaje) {
  let fechaInicio = Date.parse(viaje.fechaInicio);
  let fechaFin = Date.parse(viaje.fechaFin);
  return Math.ceil((fechaFin - fechaInicio) / (1000 * 3600 * 24));
}

export function calculaPrecioBase(reserva) {
  if (reserva.precioBasico === 0 || reserva.precioBasico === undefined) {
    return currency(reserva.precioDesdeViaje)
      .add(reserva.precioServicios)
      .add(reserva.precioComplementos);
  } else {
    return currency(reserva.precioBasico).add(reserva.precioServicios).add(reserva.precioComplementos);
  }
}

export function calculaPrecioForServicioWithCantidad(swc, relativeToPB = true) {
  let cantidad = swc.cantidad;
  let precio = swc.servicio.precio;
  if (swc.servicio.paqueteBasico) {
    cantidad--;
    precio = swc.servicio.precioTeorico;
  } else if (swc.categoriaPaqueteBasico) {
    precio = swc.servicio.precioTeorico;
  }
  precio = currency(precio).multiply(cantidad);
  if (swc.categoriaPaqueteBasico && !swc.servicio.paqueteBasico && !swc.servicio.nulo && relativeToPB) {
    precio = currency(precio).subtract(swc.servicioPB.precioTeorico);
  }
  return precio;
}

export function calculaPrecioServicios(reserva) {
  return Object.values(reserva.serviciosWithCantidad).reduce(
    (accum, swc) => calculaPrecioForServicioWithCantidad(swc, true).add(accum),
    0
  );
}

export function calculaPrecioComplementos(reserva) {
  if (reserva.complementos) {
    return Object.values(reserva.complementos).reduce(
      (accum, complemento) => accum.add(complemento.importe),
      currency(0)
    );
  } else {
    return 0;
  }
}

export function calculaPrecioGastos(reserva) {
  if (reserva.gastos) {
    return Object.values(reserva.gastos).reduce((accum, gasto) => accum.add(gasto.importe), currency(0));
  } else {
    return 0;
  }
}

export function calculaPrecioTotalReservas(reservas) {
  return reservas?.reduce((accum, reserva) => accum.add(calculaPrecioTotal(reserva)), currency(0));
}

export function calculaPrecioTotal(reserva, includeDescuento = true) {
  let precioTotal = currency(reserva.precioTotal);
  if (includeDescuento) {
    precioTotal = precioTotal.subtract(reserva.descuento || 0);
  }
  return precioTotal;
}

export function calculaPrecioTotalSinTasasInternasReservas(reservas) {
  return reservas?.reduce((accum, reserva) => accum.add(calculaPrecioTotalSinTasasInternas(reserva)), currency(0));
}

export function calculaPrecioTotalSinTasasInternas(reserva, includeDescuento = true) {
  let precioTotalSinTasasInternas = currency(reserva.precioTotalSinTasasInternas);
  if (includeDescuento) {
    precioTotalSinTasasInternas = precioTotalSinTasasInternas.subtract(reserva.descuento || 0);
  }
  return precioTotalSinTasasInternas;
}

export function calculaDescuento(descuentoData, precioBase) {
  let descuento = currency(descuentoData.cantidad);
  if (descuentoData.tipoCantidad === TIPO_CODIGO_DESCUENTO.PORCENTAJE) {
    return descuento.divide(100).multiply(precioBase);
  } else {
    return descuento;
  }
}

export function buildFormPreguntasCrearReserva() {
  const nameGrupo = generateRandomString();
  return {
    nombreGrupo: nameGrupo,
    comercialReferenciadoId: Cookies.get('comercialId') || null,
  };
}

export function buildFormCodigoCupoCrearReserva(parametrosRuta, codigoIntegracion) {
  return {
    codigoGrupo: parametrosRuta.codigoGrupo || null,
    cupo: {
      id: parametrosRuta.cupoId ? parseInt(parametrosRuta.cupoId) : null,
      precioCupoSinMarkupProrrateado: parametrosRuta.precioCupoSinMarkupProrrateado ? parseFloat(parametrosRuta.precioCupoSinMarkupProrrateado) : null,
      hotel: parseInt(parametrosRuta.hotelId),
      cacheTimestamp: parseInt(parametrosRuta.cacheTimestamp),
      tipoHabitacion: parseInt(parametrosRuta.tipoHabId),
      numPlazas: parseInt(parametrosRuta.numPlazas),
      codigoIntegracion: codigoIntegracion,
      regimen: parametrosRuta.regimen || null,
    },
  };
}

export function buildListaReservasCrearReserva(listaReservas, esModificarReserva = false) {
  const reservasDtoArray = [];

  listaReservas.forEach(reserva => {
    const serviciosAux = Object.values(reserva.serviciosWithCantidad).map(swc => {
      return { id: swc.servicio.id, cantidad: swc.cantidad };
    });
    const reservaDto = {
      reservaPropia: reserva.reservaPropia || false,
      nombre: reserva.reservaPropia ? '' : reserva.usuario.nombre,
      apellidos: reserva.reservaPropia ? '' : reserva.usuario.apellido,
      fechaNacimiento: reserva.usuario.fechaNacimiento
        ? moment(reserva.usuario.fechaNacimiento).format('YYYY-MM-DD')
        : null,
      observaciones: '',
      serviciosWithCantidad: serviciosAux,
    };

    if (esModificarReserva) {
      reservaDto.id = reserva.agregaModificar ? null : reserva.id;
    }

    reservasDtoArray.push(reservaDto);
  });

  return reservasDtoArray;
}

export function validacionNombresReservas(listaReservas) {
  const toaster = createToaster({ position: 'top-right' });
  let errorNombres = [];
  listaReservas.map(reserva => {
    if (!reserva.reservaPropia) {
      if (reserva.usuario.nombre === '') {
        toaster.error(t('errors.reserva.emptyNames'));
        errorNombres.push(true);
        return;
      }
      if (reserva.usuario.apellido === '') {
        toaster.error(t('errors.reserva.emptyLastNames'));
        errorNombres.push(true);
        return;
      }
    }
  });
  return errorNombres.findIndex(valor => valor == true) === -1;
}

